import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "react-quill/dist/quill.snow.css";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Card,
  Alert,
  Spinner,
  Modal,
} from "react-bootstrap";
import { Input } from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader";
import {
  extensionSettingsForm,
  gatewayForm,
  userForm,
} from "./configs/Forms/AccountsForms";
import { dialplanManagerForm, } from "./configs/Forms/DialplanForms";
import { domainForm, domainSettingsForm, defaultSettingsForm, modulesForm, varsForm, sofiaGlobalSettingsForm, databasesForm } from "./configs/Forms/AdvancedForms";
import { callBlockForm } from "./configs/Forms/ApplicationsForms";
import { resolveSingularName } from "./configs/Naming";
import { serverHost } from "variables/serverdata";

// Map frontend route -> { table: actual DB table, form: which form layout to use }
const formConfigs = {
  v_extension_settings: extensionSettingsForm,
  v_gateways: gatewayForm,
  v_users: userForm,
  v_dialplans: dialplanManagerForm,
  v_call_block: callBlockForm,
  v_domains: domainForm,
  v_domain_settings: domainSettingsForm,
  v_default_settings: defaultSettingsForm,
  v_modules: modulesForm,
  v_vars: varsForm,
  v_sofia_global_settings: sofiaGlobalSettingsForm,
  v_databases: databasesForm
};

const tableRoutes = {
  v_extension_settings: "extensionSettings",
  v_gateways: "gateways",
  v_users: "users",
  v_destinations: "destinations",
  v_dialplans: "dialplans",
  v_call_block: "callBlock",
  v_domains: "domains",
  v_domain_settings: "domain-settings",
  v_default_settings: "default-settings",
  v_modules: "modules",
  v_vars: "variables",
  v_sofia_global_settings: "sofiaGlobalSettings",
  v_databases: "databases"
};

const titleCase = (s) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const GenericAdd = () => {
  const { table: tableName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const formConfig = formConfigs[tableName];
  const routePath = tableRoutes[tableName] || tableName.replace("v_", "");

  const [formData, setFormData] = useState({});
  const initialFormData = useRef(null);
  const [loading, setLoading] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [error, setError] = useState("");
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);

  // Advanced modal state
  const [advancedData, setAdvancedData] = useState({});
  const advancedInitial = useRef({});

  const singular = resolveSingularName(null, tableName);

  // Initialize form data
  useEffect(() => {
    if (!formConfig) {
      setError("Invalid table");
      setLoading(false);
      return;
    }

    const initForm = async () => {
      // Start with defaults from the form config
      const defaults = { ...(formConfig.defaults || {}) };

      // Fill missing fields with empty string
      Object.keys(formConfig.labels || {}).forEach((f) => {
        if (!(f in defaults)) defaults[f] = "";
      });

      // Pre-fill extension_uuid for extension settings
      if (tableName === "v_extension_settings" && query.has("extension_uuid")) {
        const extUuid = query.get("extension_uuid");
        defaults.extension_uuid = extUuid;

        try {
          const res = await fetch(
            `${serverHost}/get_row?table=v_extensions&pk=extension_uuid&id=${encodeURIComponent(
              extUuid
            )}`
          );
          const data = await res.json();
          // Only set 'extension' if not already set
          if (!defaults.extension) defaults.extension = data.result?.extension || "";
        } catch (err) {
          console.warn("Failed to fetch extension:", err);
        }
      }

      setFormData(defaults);
      initialFormData.current = defaults;

      // Initialize advancedData with just advanced fields
      if (formConfig.sections) {
        const adv = {};
        Object.entries(formConfig.sections).forEach(([name, sec]) => {
          if (sec.advanced && sec.fields) {
            sec.fields.forEach((f) => {
              adv[f] = defaults[f];
            });
          }
        });
        setAdvancedData(adv);
        advancedInitial.current = { ...adv };
      }

      setLoading(false);
    };



    initForm();
  }, [formConfig, tableName, location.search]);



  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ---- Advanced handlers ----

  const handleAdvancedFieldChange = (field, value) => {
    setAdvancedData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAdvancedBack = () => {
    const dirty = Object.keys(advancedData).some(
      (key) => advancedData[key] !== advancedInitial.current[key]
    );

    if (dirty && !window.confirm("Discard changes to advanced options?")) return;

    setShowAdvancedModal(false);
    setAdvancedData({ ...advancedInitial.current });
  };

  const handleAdvancedSave = () => {
    // Merge advancedData into main formData
    setFormData((prev) => ({ ...prev, ...advancedData }));

    // Mark advancedData as saved baseline
    advancedInitial.current = { ...advancedData };

    setShowAdvancedModal(false);
  };

  const handleSave = async () => {
    const site = JSON.parse(localStorage.getItem("selectedSite"));
    const missingRequired = formConfig.required?.filter(
      (f) => !formData[f] || formData[f].toString().trim() === ""
    );
    if (missingRequired?.length) {
      setError(`Missing required fields: ${missingRequired.join(", ")}`);
      return;
    }

    const payloadData = { ...formData };
    if (formConfig.displayOnly?.length)
      formConfig.displayOnly.forEach((f) => delete payloadData[f]);

    if (tableName === "v_extension_settings") delete payloadData.extension;

    const user = JSON.parse(localStorage.getItem("user"));
    const insert_user = user?.user_uuid || null;
    const client_uuid = localStorage.getItem("clientId")

    const excludedDomainTables = ["v_default_settings", "v_vars", "v_access_control_nodes"];
    if (!excludedDomainTables.includes(tableName)) {
      payloadData.domain_uuid = site?.site_uuid || null;
    }

    try {
      const payload = {
        table: tableName,
        data: formData,
        site_uuid: site.site_uuid,
        client_uuid: client_uuid,
        module_name: "HUCS",
        module_uuid: "35525bfe-5ee5-4ef7-8bd6-560d489803d5",
        formConfig: formConfig,
        insert_user
      };
      let url = `${serverHost}/insert`
      if (tableName === "v_domains") url = `${serverHost}/create_domain`  
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let fail = "";
        if (res.status === 403) fail = "; Limit reached";
        throw new Error("Failed to insert record" + fail);
      }

      let openSection = null;
      if (formConfig.groupBy) {
        openSection = formData[formConfig.groupBy];
      }
      navigate(`/admin/hucs/${routePath}`, { state: { openSection } });

    } catch (err) {
      console.log(err);
      setError(err.message);
    }
  };

  const handleBack = () => {
    const isDirty = Object.keys(formData || {}).some(
      (key) => formData[key] !== initialFormData.current?.[key]
    );

    if (isDirty && !window.confirm("Discard changes and go back?")) return;

    navigate(`/admin/hucs/${routePath}`);
  };

  const renderField = (tableConfig, field, value, onChange) => {
    const type = tableConfig.types?.[field] || "text";
    const label =
      tableConfig.labels?.[field] || titleCase(field);

    const placeholder = value
      ? ""
      : `Enter ${label.toLowerCase()}`;

    switch (type) {
      case "boolean":
        return (
          <Input
            type="select"
            value={
              value === "t" || value === "true" || value === true ? "true" :
                value === "f" || value === "false" || value === false ? "false" :
                  ""
            }
            onChange={(e) =>
              onChange(e.target.value === "true" ? "true" : "false")
            }
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </Input>
        );

      case "select":
        if (loadingOptions) return <Spinner animation="border" size="sm" />;

        const staticNormalized = (tableConfig.options?.[field] || []).map((opt) =>
          typeof opt === "object" ? opt : { label: opt, value: opt }
        );

        // Merge & dedupe by value
        const merged = staticNormalized.filter(
          (opt, index, arr) =>
            index === arr.findIndex((o) => o.value === opt.value)
        );

        // Ensure current value always exists
        if (value && !merged.some((opt) => opt.value === value)) {
          merged.push({ label: value, value });
        }

        return (
          <Input
            type="select"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Select...</option>
            {merged.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Input>
        );

      case "number":
        return (
          <Input
            type="number"
            value={value ?? ""}
            onChange={(e) => onChange(Number(e.target.value))}
          />
        );


      case "readonly":
        return (
          <Input
            type="text"
            value={value ?? ""}
            readOnly
            className="bg-light cursor-default"
          />
        );

      case "textarea":
        return (
          <Input
            type="textarea"
            rows={10}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            style={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
          />
        );

      default:
        return (
          <Input
            type="text"
            value={value ?? ""}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading form...</p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error}
      </Alert>
    );

  // SAFE SECTION HANDLING 
  const hasSections =
    formConfig.sections && Object.keys(formConfig.sections).length > 0;

  let basicSections = [];
  let advancedSections = [];

  if (hasSections) {
    advancedSections = Object.entries(formConfig.sections).filter(
      ([_, section]) => typeof section === "object" && section.advanced
    );

    basicSections = Object.entries(formConfig.sections).filter(
      ([_, section]) => !section.advanced
    );
  } else {
    // No sections → treat all fields as one flat section
    const allFields = Object.keys(formConfig.labels || {});
    basicSections = [["", allFields]];
  }

  // ----- COMPONENT RENDER -----
  return (
    <>
      <SimpleHeader name={`Add ${singular}`} parentName="HUCS" />

      <Container className="mt--6">
        <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
          <Card.Header className="header-theme">
            <h4 className="mb-0">ADD {singular.toUpperCase()}</h4>
          </Card.Header>

          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}

            {/* Advanced button only if sections exist */}
            {advancedSections.length > 0 && (
              <Button
                variant="info"
                className="mb-4"
                onClick={() => setShowAdvancedModal(true)}
              >
                ADVANCED SETTINGS
              </Button>
            )}

            <Form
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSave();
                }
              }}
            >
              {/* BASIC SECTIONS */}
              {basicSections.map(([sectionName, fields]) => (
                <div key={sectionName || "basic"} className="mb-4">
                  {/* Hide title if blank */}
                  {sectionName && (
                    <h5
                      className="section-title mb-3"
                      style={{ fontSize: "1.5rem", fontWeight: 700 }}
                    >
                      {sectionName}
                    </h5>
                  )}

                  <Row>
                    {fields.map((f) => {
                      if (
                        f === "missed_call_email" &&
                        formData.missed_call_app !== "Email"
                      ) {
                        return null;
                      }

                      return (
                        <Col md={4} key={f}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {formConfig.labels?.[f] ||
                                f
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (l) => l.toUpperCase())}

                              {formConfig.required?.includes(f) && (
                                <span style={{ color: "red" }}> *</span>
                              )}
                            </Form.Label>

                            {renderField(formConfig, f, formData[f], (val) =>
                              handleChange(f, val)
                            )}
                          </Form.Group>
                        </Col>
                      );
                    })}
                  </Row>

                </div>
              ))}

              <div className="mt-4 d-flex justify-content-between">
                <Button variant="danger" onClick={handleBack}>
                  Cancel
                </Button>

                <Button
                  variant="success"
                  onClick={handleSave}
                  disabled={formConfig.required?.some(
                    (f) =>
                      !formData[f] || formData[f].toString().trim() === ""
                  )}
                >
                  Save
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>

      {/* ADVANCED OPTIONS MODAL */}
      <Modal
        show={showAdvancedModal}
        onHide={handleAdvancedBack}
        size="lg"
        centered
      >
        <Modal.Header closeButton="lg">
          <Modal.Title>Advanced Options</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {advancedSections.map(([sectionName, sectionData]) => (
            <Card key={sectionName} className="mb-4">
              <Card.Header>
                <strong>{sectionName}</strong>
              </Card.Header>

              <Card.Body>
                <Row>
                  {sectionData.fields.map((f) => (
                    <Col md={4} key={f}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          {formConfig.labels?.[f] ||
                            f
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                        </Form.Label>

                        {renderField(
                          formConfig,
                          f,
                          advancedData[f],
                          (val) => handleAdvancedFieldChange(f, val)
                        )}
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          ))}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="danger" onClick={handleAdvancedBack}>
            Cancel
          </Button>

          <Button variant="success" onClick={handleAdvancedSave}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GenericAdd;